import React, { useState, useRef, useEffect } from 'react';
import jsQR from 'jsqr';
import { API_ENDPOINTS } from '../config/api';
import { authenticatedFetch } from '../config/authenticatedFetch';
import './CheckinTunel.css';

interface RegistrationData {
  id?: string;
  external_id: string;
  name: string;
  phrase: string;
  phrase2: string;
  speciality: string;
  type_code?: string | null;
  photo_url?: string;
}

const CheckinTunel: React.FC = () => {
  const [isScanning, setIsScanning] = useState<boolean>(true); // Iniciar com scanner aberto
  const [scannedCode, setScannedCode] = useState<string>('');
  const [qrDetected, setQrDetected] = useState<boolean>(false);
  const [scannerDetected, setScannerDetected] = useState<boolean>(false); // Para efeito visual
  const [registrationData, setRegistrationData] = useState<RegistrationData | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [hasValidCode, setHasValidCode] = useState<boolean>(false);
  const [checkinConfirmed, setCheckinConfirmed] = useState<boolean>(false);
  const [processingCheckin, setProcessingCheckin] = useState<boolean>(false);
  const [showSuccessScreen, setShowSuccessScreen] = useState<boolean>(false);
  const [showErrorScreen, setShowErrorScreen] = useState<boolean>(false);
  const [csrfToken, setCsrfToken] = useState<string>('');
  
  const [shouldStartScanning, setShouldStartScanning] = useState<boolean>(true); // Iniciar automaticamente

  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const scanIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Função para tocar som de feedback
  const playDetectionSound = () => {
    try {
      // Criar um som simples usando Web Audio API
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.setValueAtTime(800, audioContext.currentTime);
      oscillator.frequency.setValueAtTime(1000, audioContext.currentTime + 0.1);
      
      gainNode.gain.setValueAtTime(0.1, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.2);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.2);
    } catch (error) {
      console.warn('⚠️ Erro ao tocar som de feedback:', error);
    }
  };

  // Função para obter token CSRF do backend
  const fetchCSRFToken = async () => {
    try {
      console.log('🔐 Buscando token CSRF...');
      const response = await authenticatedFetch(API_ENDPOINTS.API_CSRF_TOKEN, {
        method: 'GET',
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        console.log('✅ Token CSRF obtido');
        setCsrfToken(data.csrfToken);
        return data.csrfToken;
      } else {
        console.warn('⚠️ Não foi possível obter token CSRF');
        return null;
      }
    } catch (error) {
      console.error('❌ Erro ao buscar token CSRF:', error);
      return null;
    }
  };

  // useEffect para permitir scroll nesta página e buscar CSRF token
  useEffect(() => {
    // Adicionar classe para permitir scroll no html e body
    document.documentElement.classList.add('allow-scroll');
    document.body.classList.add('allow-scroll');
    
    // Debug: verificar se as classes foram aplicadas
    console.log('🔄 Classes aplicadas:', {
      html: document.documentElement.classList.contains('allow-scroll'),
      body: document.body.classList.contains('allow-scroll')
    });
    
    // Buscar token CSRF
    fetchCSRFToken();
    
    // Inicializar scanner automaticamente após um breve delay
    const timer = setTimeout(() => {
      setShouldStartScanning(true);
    }, 500);
    
    return () => {
      clearTimeout(timer);
      
      // Remover classe ao sair da página
      document.documentElement.classList.remove('allow-scroll');
      document.body.classList.remove('allow-scroll');
      
      console.log('🔄 Classes removidas - scroll bloqueado novamente');
      
      // Cleanup da câmera
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      if (scanIntervalRef.current) {
        clearInterval(scanIntervalRef.current);
      }
    };
  }, []);

  // useEffect para inicializar câmera quando o DOM está pronto
  useEffect(() => {
    if (shouldStartScanning && videoRef.current && canvasRef.current) {
      initializeCamera();
    }
  }, [shouldStartScanning, isScanning]);

  const initializeCamera = async () => {
    try {
      console.log('🚀 Inicializando câmera...');
      
      // Verificar se getUserMedia está disponível
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        setError('Câmera não suportada neste navegador');
        setIsScanning(false);
        setShouldStartScanning(false);
        return;
      }

      console.log('📱 Solicitando acesso à câmera...');
      
      const constraints = {
        video: {
          facingMode: { ideal: 'environment' }, // Preferir câmera traseira
          width: { ideal: 640 },
          height: { ideal: 480 }
        }
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      console.log('✅ Stream da câmera obtido:', stream);
      
      streamRef.current = stream;
      
      // Aguardar o video estar pronto
      const video = videoRef.current!;
      video.srcObject = stream;
      
      // Aguardar o vídeo carregar
      video.onloadedmetadata = () => {
        console.log('📹 Metadados do vídeo carregados');
        video.play().then(() => {
          console.log('▶️ Vídeo iniciado com sucesso');
          
          // Iniciar detecção de QR code com frequência otimizada (3x por segundo)
          scanIntervalRef.current = setInterval(() => {
            captureFrame();
          }, 330); // ~3 FPS para melhor performance
          
          setShouldStartScanning(false); // Reset flag
          
        }).catch((playError) => {
          console.error('❌ Erro ao reproduzir vídeo:', playError);
          setError('Erro ao iniciar visualização da câmera');
          stopScanning();
        });
      };

    } catch (err: any) {
      console.error('❌ Erro ao acessar câmera:', err);
      
      let errorMessage = 'Erro ao acessar a câmera';
      
      if (err.name === 'NotAllowedError') {
        errorMessage = 'Permissão para acessar a câmera foi negada. Permita o acesso e tente novamente.';
      } else if (err.name === 'NotFoundError') {
        errorMessage = 'Nenhuma câmera foi encontrada no dispositivo.';
      } else if (err.name === 'NotReadableError') {
        errorMessage = 'Câmera está sendo usada por outro aplicativo.';
      } else if (err.name === 'OverconstrainedError') {
        errorMessage = 'Câmera não atende aos requisitos especificados.';
      }
      
      setError(errorMessage);
      setIsScanning(false);
      setShouldStartScanning(false);
    }
  };

  const startScanning = async () => {
    try {
      setError('');
      setSuccess('');
      setQrDetected(false); // Reset QR detection state
      setIsScanning(true);
      console.log('🚀 Iniciando scanner...');
      
      // Aguardar um breve momento para o DOM se atualizar
      await new Promise(resolve => setTimeout(resolve, 100));
      
      if (!videoRef.current || !canvasRef.current) {
        console.error('❌ Referências de video ou canvas não encontradas após renderização');
        setError('Erro interno: elementos não encontrados');
        setIsScanning(false);
        return;
      }

      // Verificar se getUserMedia está disponível
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        setError('Câmera não suportada neste navegador');
        setIsScanning(false);
        return;
      }

      console.log('📱 Solicitando acesso à câmera...');
      
      const constraints = {
        video: {
          facingMode: { ideal: 'environment' }, // Preferir câmera traseira
          width: { ideal: 640 },
          height: { ideal: 480 }
        }
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      console.log('✅ Stream da câmera obtido:', stream);
      
      streamRef.current = stream;
      
      // Aguardar o video estar pronto
      const video = videoRef.current;
      video.srcObject = stream;
      
      // Aguardar o vídeo carregar
      video.onloadedmetadata = () => {
        console.log('📹 Metadados do vídeo carregados');
        video.play().then(() => {
          console.log('▶️ Vídeo iniciado com sucesso');
          
          // Iniciar detecção de QR code com frequência otimizada (3x por segundo)
          scanIntervalRef.current = setInterval(() => {
            captureFrame();
          }, 330); // ~3 FPS para melhor performance
          
        }).catch((playError) => {
          console.error('❌ Erro ao reproduzir vídeo:', playError);
          setError('Erro ao iniciar visualização da câmera');
          stopScanning();
        });
      };

    } catch (err: any) {
      console.error('❌ Erro ao acessar câmera:', err);
      
      let errorMessage = 'Erro ao acessar a câmera';
      
      if (err.name === 'NotAllowedError') {
        errorMessage = 'Permissão para acessar a câmera foi negada. Permita o acesso e tente novamente.';
      } else if (err.name === 'NotFoundError') {
        errorMessage = 'Nenhuma câmera foi encontrada no dispositivo.';
      } else if (err.name === 'NotReadableError') {
        errorMessage = 'Câmera está sendo usada por outro aplicativo.';
      } else if (err.name === 'OverconstrainedError') {
        errorMessage = 'Câmera não atende aos requisitos especificados.';
      }
      
      setError(errorMessage);
      setIsScanning(false);
    }
  };

  const captureFrame = () => {
    if (!videoRef.current || !canvasRef.current) {
      console.warn('⚠️ Video ou canvas não disponível para captura');
      return;
    }
    
    const canvas = canvasRef.current;
    const video = videoRef.current;
    const context = canvas.getContext('2d');
    
    if (!context) {
      console.warn('⚠️ Contexto 2D do canvas não disponível');
      return;
    }
    
    // Verificar se o vídeo tem dimensões válidas
    if (video.videoWidth === 0 || video.videoHeight === 0) {
      console.warn('⚠️ Vídeo ainda não tem dimensões válidas');
      return;
    }
    
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    context.drawImage(video, 0, 0, canvas.width, canvas.height);
    
    // Obter dados da imagem para processamento de QR code
    try {
      const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
      
      // Tentar detectar QR code usando jsQR
      const qrCode = jsQR(imageData.data, imageData.width, imageData.height, {
        inversionAttempts: "dontInvert",
      });
      
      if (qrCode) {
        console.log('🎯 QR Code detectado:', qrCode.data);
        
        // Tocar som de feedback
        playDetectionSound();
        
        // Ativar efeito visual de detecção
        setScannerDetected(true);
        setQrDetected(true);
        
        // Processar o QR code detectado (mas manter o scanner ativo)
        handleQRCodeDetected(qrCode.data);
        
        // Reset dos efeitos visuais após um tempo
        setTimeout(() => {
          setScannerDetected(false);
          setQrDetected(false);
        }, 1500);
        
        return; // Sair da função após detectar
      }
      
      // Log periódico para debug (a cada 20 tentativas - ~20 segundos)
      if (Math.random() < 0.05) {
        console.log(`📸 Frame processado: ${imageData.width}x${imageData.height}, nenhum QR code encontrado`);
      }
      
    } catch (error) {
      console.warn('⚠️ Erro ao processar frame para QR code:', error);
    }
  };

  const stopScanning = () => {
    console.log('⏹️ Parando scanner...');
    
    if (streamRef.current) {
      console.log('📷 Parando tracks da câmera');
      streamRef.current.getTracks().forEach(track => {
        track.stop();
        console.log(`🔒 Track ${track.kind} parado`);
      });
      streamRef.current = null;
    }
    
    if (scanIntervalRef.current) {
      console.log('⏰ Limpando interval de scan');
      clearInterval(scanIntervalRef.current);
      scanIntervalRef.current = null;
    }
    
    if (videoRef.current) {
      console.log('📺 Limpando srcObject do vídeo');
      videoRef.current.srcObject = null;
    }
    
    setIsScanning(false);
    console.log('✅ Scanner parado com sucesso');
  };

  const handleManualCodeInput = (code: string) => {
    if (code.trim()) {
      setScannedCode(code.trim());
      handleQRCodeDetected(code.trim());
    }
  };

  const testCameraPermissions = async () => {
    try {
      console.log('🔍 Testando permissões da câmera...');
      
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        setError('API de mídia não suportada neste navegador');
        return;
      }

      // Listar dispositivos disponíveis
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = devices.filter(device => device.kind === 'videoinput');
      
      console.log('📱 Dispositivos de vídeo encontrados:', videoDevices.length);
      videoDevices.forEach((device, index) => {
        console.log(`📹 Dispositivo ${index + 1}: ${device.label || 'Câmera ' + (index + 1)}`);
      });

      if (videoDevices.length === 0) {
        setError('Nenhuma câmera encontrada no dispositivo');
        return;
      }

      // Testar acesso básico
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      console.log('✅ Permissão concedida, câmera acessível');
      
      // Parar imediatamente o teste
      stream.getTracks().forEach(track => track.stop());
      
      setSuccess('✅ Câmera testada com sucesso! Você pode usar o scanner.');
      
    } catch (err: any) {
      console.error('❌ Erro no teste da câmera:', err);
      let errorMessage = 'Erro ao testar câmera: ';
      
      if (err.name === 'NotAllowedError') {
        errorMessage += 'Permissão negada. Permita o acesso à câmera nas configurações do navegador.';
      } else if (err.name === 'NotFoundError') {
        errorMessage += 'Nenhuma câmera encontrada.';
      } else {
        errorMessage += err.message;
      }
      
      setError(errorMessage);
    }
  };

  const handleQRCodeDetected = async (code: string) => {
    // Evitar processamento duplicado do mesmo código
    if (code === scannedCode && (registrationData || loading)) {
      console.log('🔄 Código já sendo processado ou já processado:', code);
      return;
    }
    
    setLoading(true);
    setError('');
    setSuccess('');
    setScannedCode(code); // Salvar o código escaneado

    try {
      console.log('🔍 QR Code processando:', code);
      
      // Tentar extrair ID do participante de diferentes formatos de QR code
      let participantId = code;
      
      // Se for JSON, tentar extrair o ID
      try {
        const jsonData = JSON.parse(code);
        if (jsonData.idParticipante) {
          participantId = jsonData.idParticipante;
        } else if (jsonData.id) {
          participantId = jsonData.id;
        } else if (jsonData.external_id) {
          participantId = jsonData.external_id;
        }
        console.log('📋 ID extraído do JSON:', participantId);
      } catch (e) {
        // Não é JSON, usar como string simples
        console.log('📝 Usando código como string simples:', participantId);
      }

      console.log('🔎 Buscando registro para ID:', participantId);
      const response = await authenticatedFetch(`${API_ENDPOINTS.API_GETBYID}?idParticipante=${participantId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      const data = await response.json();
      console.log('📊 Resposta da API:', data);

      if (data.success && data.data) {
        // Registro encontrado - realizar checkin automaticamente
        setRegistrationData(data.data);
        setHasValidCode(true); // Marca que um código válido foi processado
        
        // Realizar checkin automaticamente
        await performAutoCheckin(data.data.external_id);
      } else {
        // Registro não encontrado - mostrar tela vermelha
        setRegistrationData(null);
        showErrorMessage('Código inválido ou não encontrado');
        setHasValidCode(true); // Mesmo assim marca como código válido (foi processado)
      }
    } catch (err) {
      console.error('❌ Erro ao buscar registro:', err);
      showErrorMessage('Erro de conexão. Tente novamente');
    } finally {
      setLoading(false);
    }
  };

  const showErrorMessage = (message: string) => {
    // Parar o scanner e mostrar tela vermelha
    stopScanning();
    setShowErrorScreen(true);
    setError(message);
    
    // Voltar ao scanner após 2 segundos
    setTimeout(async () => {
      setShowErrorScreen(false);
      
      // Pequeno delay para garantir que a interface seja atualizada
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Limpar dados e reativar scanner
      clearData();
      setIsScanning(true);
      setShouldStartScanning(true);
    }, 2000);
  };

  const clearData = () => {
    setScannedCode('');
    setRegistrationData(null);
    setError('');
    setSuccess('');
    setHasValidCode(false); // Reset da validação do código
    setCheckinConfirmed(false); // Reset da confirmação do checkin
    setProcessingCheckin(false);
    setQrDetected(false); // Reset da detecção do QR
    // Não parar o scanner - ele continua ativo
  };

  const confirmCheckin = async () => {
    if (!registrationData) return;
    
    setProcessingCheckin(true);
    setError('');
    setSuccess('');
    
    try {
      console.log('🔄 Confirmando checkin para:', registrationData.external_id);
      
      // Garantir que temos o token CSRF
      let currentToken = csrfToken;
      if (!currentToken) {
        console.log('🔄 Token CSRF não encontrado, buscando novamente...');
        currentToken = await fetchCSRFToken();
        if (!currentToken) {
          setError('Erro ao obter token de segurança. Tente novamente.');
          return;
        }
      }
      
      const checkinData = {
        idParticipante: registrationData.external_id,
        tipo_checkin: "painel"
      };
      
      // Headers com token CSRF
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      if (currentToken) {
        headers['X-CSRFToken'] = currentToken;
      }
      
      const response = await authenticatedFetch(API_ENDPOINTS.API_CHECKIN, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify(checkinData)
      });

      const data = await response.json();
      console.log('📊 Resposta do checkin:', data);

      if (response.ok && data.success) {
        setCheckinConfirmed(true);
        setSuccess('✅ Check-in realizado com sucesso! Bem-vindo ao Túnel!');
      } else {
        setError(data.message || 'Erro ao realizar check-in. Tente novamente.');
      }
    } catch (err) {
      console.error('❌ Erro ao confirmar checkin:', err);
      setError('Erro ao conectar com o servidor. Verifique a conexão.');
    } finally {
      setProcessingCheckin(false);
    }
  };

  const performAutoCheckin = async (participantId: string) => {
    console.log('🔄 Realizando checkin automático para:', participantId);
    
    try {
      // Garantir que temos o token CSRF
      let currentToken = csrfToken;
      if (!currentToken) {
        console.log('🔄 Token CSRF não encontrado, buscando novamente...');
        currentToken = await fetchCSRFToken();
        if (!currentToken) {
          setError('Erro ao obter token de segurança. Tente novamente.');
          return;
        }
      }
      
      const checkinData = {
        idParticipante: participantId,
        tipo_checkin: "painel"
      };
      
      // Headers com token CSRF
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      if (currentToken) {
        headers['X-CSRFToken'] = currentToken;
      }
      
      const response = await authenticatedFetch(API_ENDPOINTS.API_CHECKIN, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify(checkinData)
      });

      const data = await response.json();
      console.log('📊 Resposta do checkin automático:', data);

      if (response.ok && data.success) {
        setCheckinConfirmed(true);
        setSuccess('✅ Check-in realizado automaticamente! Bem-vindo ao Túnel!');
        
        // Mostrar tela de sucesso e pausar scanner por 3 segundos
        stopScanning(); // Para o scanner e libera a câmera
        setShowSuccessScreen(true);
        
        // Voltar ao scanner após 3 segundos
        setTimeout(async () => {
          setShowSuccessScreen(false);
          
          // Pequeno delay para garantir que a interface seja atualizada
          await new Promise(resolve => setTimeout(resolve, 100));
          
          // Limpar dados e reativar scanner
          clearData();
          setIsScanning(true);
          setShouldStartScanning(true);
        }, 2000);
      } else {
        showErrorMessage(data.message || 'Erro no check-in. Tente novamente');
        
        // Limpar dados após erro também para permitir nova tentativa
        setTimeout(() => {
          clearData();
        }, 2000);
      }
    } catch (err) {
      console.error('❌ Erro ao realizar checkin automático:', err);
      showErrorMessage('Erro de conexão. Verifique a rede');
      
      // Limpar dados após erro também para permitir nova tentativa
      setTimeout(() => {
        clearData();
      }, 3000);
    }
  };

  const cancelCheckin = () => {
    console.log('❌ Checkin cancelado pelo usuário');
    // Apenas limpar os dados, manter o scanner ativo
    setScannedCode('');
    setRegistrationData(null);
    setError('');
    setSuccess('');
    setHasValidCode(false);
    setCheckinConfirmed(false);
    setProcessingCheckin(false);
    setQrDetected(false);
  };

  return (
    <div className="checkin-tunel-container">
      {/* Tela de Sucesso */}
      {showSuccessScreen && (
        <div className="success-screen">
          <div className="success-content">
            <div className="success-icon">✅</div>
            <h2>Check-in Realizado!</h2>
            <p>Bem-vindo ao Túnel de Fotos</p>
            <div className="success-animation"></div>
          </div>
        </div>
      )}

      {/* Tela de Erro */}
      {showErrorScreen && (
        <div className="error-screen">
          <div className="error-content">
            <div className="error-icon">❌</div>
            <h2>Código Inválido</h2>
            <p>{error || 'Tente novamente'}</p>
            <div className="error-animation"></div>
          </div>
        </div>
      )}

      {/* Conteúdo Principal - oculto quando tela de sucesso ou erro está ativa */}
      {!showSuccessScreen && !showErrorScreen && (
        <>
          <div className="checkin-tunel-header">
            <h1>🎯 Check-in Túnel</h1>
            <p>Escaneie um QR code para fazer check-in no Túnel de Fotos</p>
          </div>

      {/* Scanner Section */}
      <div className="scanner-section">
        <div className="scanner-controls">
          <div className="button-group">
            <button
              onClick={testCameraPermissions}
              className="btn btn-outline"
              disabled={loading}
            >
              🔧 Testar Câmera
            </button>
            
            <div className="scanner-status">
              {isScanning ? (
                <span className="status-active">📷 Scanner Ativo</span>
              ) : (
                <button 
                  onClick={startScanning}
                  className="btn btn-primary"
                  disabled={loading}
                >
                  📷 Reiniciar Scanner
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Input manual para teste */}
        <div className="manual-input-section">
          <h3>Ou digite o código manualmente:</h3>
          <div className="manual-input-group">
            <input
              type="text"
              placeholder="Digite o código (ex: USER_002)"
              className="form-control"
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleManualCodeInput((e.target as HTMLInputElement).value);
                  (e.target as HTMLInputElement).value = '';
                }
              }}
            />
            <button
              onClick={() => {
                const input = document.querySelector('.manual-input-group input') as HTMLInputElement;
                if (input) {
                  handleManualCodeInput(input.value);
                  input.value = '';
                }
              }}
              className="btn btn-primary"
              disabled={loading}
            >
              🔍 Buscar
            </button>
          </div>
        </div>

        {isScanning && (<center>
          <div className="scanner-container">
            <div className="scanner-status">
              <div className={`status-indicator ${scannerDetected ? 'detected' : 'scanning'}`}>
                {scannerDetected ? '🎯 QR Code Detectado!' : '📱 Escaneando...'}
              </div>
            </div>
            <video 
              ref={videoRef}
              className="scanner-video"
              autoPlay
              playsInline
              muted
            />
            <canvas 
              ref={canvasRef}
              style={{ display: 'none' }}
            />
            {/* <div className="scanner-overlay">
              <div className={`scanner-frame ${scannerDetected ? 'detected' : ''}`}> */}
                <div className="scan-line"></div>
              {/* </div>
            </div> */}
          </div></center>
        )}

        {scannedCode && (
          <div className="scanned-result">
            <strong>Código detectado:</strong> {scannedCode}
          </div>
        )}
      </div>

      {/* Messages */}
      {loading && <div className="message loading">⏳ {registrationData ? 'Realizando check-in...' : 'Buscando participante...'}</div>}
      {error && <div className="message error">❌ {error}</div>}
      {success && <div className="message success">✅ {success}</div>}
      
      {/* Warning message when no valid code */}
      {!hasValidCode && !loading && (
        <div className="message warning">
          ⚠️ É necessário escanear um QR code ou inserir um código manualmente para fazer check-in.
        </div>
      )}

      {/* Data Display Section - Checkin Confirmed */}
      {registrationData && hasValidCode && (
        <div className="data-section">
          <div className="data-header">
            <h2>🎉 Check-in Confirmado!</h2>
            <div className="record-info">
              <small>ID: {registrationData.id}</small>
            </div>
          </div>

          <div className="participant-info">
            <div className="welcome-message">
              <h3>Bem-vindo ao Túnel de Fotos!</h3>
              <p>Seu check-in foi realizado automaticamente.</p>
            </div>

            <div className="info-row">
              <div className="info-group">
                <label>ID Participante:</label>
                <div className="info-value">{registrationData.external_id}</div>
              </div>
              <div className="info-group">
                <label>Nome:</label>
                <div className="info-value">{registrationData.name || 'Não informado'}</div>
              </div>
            </div>

            <div className="info-row">
              <div className="info-group">
                <label>Especialidade:</label>
                <div className="info-value">{registrationData.speciality || 'Não informado'}</div>
              </div>
            </div>

            {registrationData.photo_url && (
              <div className="info-group photo-section">
                <label>Sua Foto:</label>
                <div className="photo-display">
                  <img 
                    src={registrationData.photo_url} 
                    alt="Foto do participante" 
                    className="participant-photo"
                  />
                </div>
              </div>
            )}

            <div className="actions">
              <button
                onClick={clearData}
                className="btn btn-outline"
              >
                🔄 Próximo Participante
              </button>
            </div>
          </div>
        </div>
      )}
        </>
      )}
    </div>
  );
};

export default CheckinTunel;
